import realMoment from 'moment';
import { fullDateAtTime, shortDateAtTime, hourMin } from 'Consts/DateFormats';

// From Go: '2009-11-10 23:00:00 -0500 (EST) m=+0.000000001'
// For JS:  '2009-11-10 23:00:00-0500'

// NOTE: These are not perfect regexes, but they do what we need for now.
const isString = d => typeof d === 'string';
const isGoDate = d => isString(d)
  && d.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\s-\d{4}.*/) !== null;
const isTimeless = d => isString(d) && d.match(/^\d{4}-\d{2}-\d{2}&/) !== null;
const isIsoDate = d => isString(d)
  && d.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}-\d{4}$/) !== null;

// Deprecated
const moment = (str) => {
  if (str instanceof Date) {
    return realMoment(str);
  }
  if (str instanceof realMoment) {
    return str;
  }
  if (str === null || str === undefined) {
    return realMoment();
  }
  if (isTimeless(str) || isIsoDate(str)) {
    return realMoment(Date.parse(str));
  }

  if (isGoDate(str)) {
    const split = str.split(' ');
    let newString = [split[0], split[1]].join(' ');
    newString += split[2];
    return realMoment(newString);
  }

  return realMoment(str);
};

const getTimestampFromString = date => (date ? parseInt(realMoment(date).format('x'), 10) : null);

const getTimezoneOffsetInHours = () => new Date().getTimezoneOffset() * -1 / 60;

// The server sends out dates formatted in UTC time. Maintain the same date but set the time to start of day locally.
// IE. 2021-02-03T12:00:00Z -> 2021-02-03T00:00:00-5:00
const getStartOfDayLocalFromUTC = (dateISOString) => {
  const localMoment = realMoment(dateISOString);
  const tzOffsetMinutes = localMoment.utcOffset();
  const startOfDayLocalInUTC = realMoment.utc(dateISOString || undefined)
    .startOf('day')
    .add(-1 * tzOffsetMinutes, 'minutes')
    .toISOString();

  return realMoment(startOfDayLocalInUTC).toISOString(true);
};

// Convert from a local time to the start of day in UTC while maintaining the same date.
// IE. 2021-02-03T12:00:00-5:00 -> 2021-02-03T00:00:00
const getStartOfDayUTCFromLocal = (dateISOString) => {
  const startOfDayLocal = realMoment(dateISOString || undefined).startOf('day');
  const tzOffsetMinutes = startOfDayLocal.utcOffset();
  const adjusted = startOfDayLocal.add(tzOffsetMinutes, 'minutes');
  return adjusted.toISOString();
};

const isAfterTodayUTC = (date) => {
  return realMoment.utc(date).isAfter(realMoment.utc().endOf('day'));
};

const isBeforeTodayUTC = (date) => {
  return realMoment.utc(date).isBefore(realMoment.utc());
};

const isTodayUTC = (date) => {
  return realMoment().isSame(realMoment.utc(date), 'day'); // date is today
};

const getTimeOfDay = (date) => {
  return realMoment(date).format(hourMin);
};

const getTimeZone = (date) => {
  return date.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop();
};

const getTimeOfDayWithTimezone = (date) => {
  const d = new Date(date);
  const timezoneName = getTimeZone(d);
  return `${realMoment(date).format(hourMin)} ${timezoneName}`;
};

const getRelativeTime = (date) => {
  let relativeTime;
  const d = realMoment(date);

  const formatTimeAgo = (diff, type) => {
    const isSingular = diff === 1;
    return `${diff} ${type}${!isSingular ? 's' : ''} ago`;
  };

  // Determines datetime difference
  const hourDiff = realMoment().diff(d, 'hours');
  const daysDiff = realMoment().diff(d, 'days');
  const monthsDiff = realMoment().diff(d, 'months');
  const yearsDiff = realMoment().diff(d, 'years');

  // Determines relative time
  const isSameDay = realMoment().isSame(d, 'day'); // date is today --> Show time
  const isWithinADay = hourDiff < 24; // date is < 24h --> show hrs ago
  const isWithinAMonth = daysDiff < 28; // date is < 28d --> show days ago
  const isWithinAYear = monthsDiff < 12; // date is < 12m --> show months ago

  // Set relative time to time-ago
  if (isSameDay) {
    relativeTime = d.format(hourMin);
  } else if (isWithinADay) {
    relativeTime = formatTimeAgo(hourDiff, 'hour');
  } else if (isWithinAMonth) {
    relativeTime = formatTimeAgo(daysDiff, 'day');
  } else if (isWithinAYear) {
    relativeTime = formatTimeAgo(monthsDiff, 'month');
  } else {
    relativeTime = formatTimeAgo(yearsDiff, 'year');
  }

  return relativeTime;
};

const isCurrentYear = (date) => {
  const d = realMoment(date);
  const isWithinAYear = realMoment().diff(d, 'year') === 0;
  return isWithinAYear;
};

const daysInMonth = (date = null) => {
  const d = !date ? new Date() : new Date(date);
  return realMoment(d).daysInMonth();
};

const getFormattedDateWithTimezone = (date, format = null) => {
  const defaultFormat = (isCurrentYear(date) ? shortDateAtTime : fullDateAtTime);
  const fmt = !format ? defaultFormat : format;
  const d = new Date(date);
  const timezoneName = getTimeZone(d);
  return `${realMoment(date).format(fmt)} ${timezoneName}`;
};

export {
  moment,
  getTimestampFromString,
  isString,
  isGoDate,
  isTimeless,
  isIsoDate,
  getTimezoneOffsetInHours,
  getStartOfDayLocalFromUTC,
  getStartOfDayUTCFromLocal,
  isAfterTodayUTC,
  isBeforeTodayUTC,
  isTodayUTC,
  getTimeOfDay,
  getTimeOfDayWithTimezone,
  getRelativeTime,
  isCurrentYear,
  daysInMonth,
  getFormattedDateWithTimezone,
};
